import React, { PureComponent } from 'react';
import { csvParse, utcParse, utcYear, nest, curveStepAfter, scaleOrdinal, sum, ascending, max } from 'd3'
import { createSelector } from 'reselect';
//import Selector from './Selector';
import BreakdownPie from './BreakdownPie';
import BreakdownTable from './BreakdownTable';
import Timeline from './Timeline';
import { translate } from './translate';
import { DATE, HOLDING_WEIGHT, HOLDING_NAME } from './constants';
import { qualitativeColors, otherColor } from './pallette';
import './HoldingsWidget.css';

const REMAINING = 'Remaining Portfolio'

// Limit the number of holdings. If a fund has more than this many holdings,
// the remaining ones will be aggregated into REMAINING
const MAX_NUM_HOLDINGS_TO_SHOW = 10

const parseDate = utcParse('%-m/%-d/%Y')
const colorScale = scaleOrdinal()
  .range(qualitativeColors)

const nestedHoldingsData = createSelector(
  (props, state) => state.data,
  (props, state) => max(state.data, d => d[DATE].getUTCFullYear()),// find the most recent year
  (data, mostCurrentYear) => nest()
    .key(d => d[DATE].getUTCFullYear() === mostCurrentYear ? 'Current' : d[DATE].getUTCFullYear())
    .entries(data)
    .map(d => ({
      ...d, 
      values: d.values
        .sort((a, b) => ascending(b[HOLDING_WEIGHT], a[HOLDING_WEIGHT]))
        .slice(0, MAX_NUM_HOLDINGS_TO_SHOW)
      }))
    .map(({key, values}) => ({
      key,
      values: values.concat([{
        [DATE]: values[0][DATE], 
        [HOLDING_NAME]: REMAINING,
        [HOLDING_WEIGHT]: 1 - sum(values, d => d[HOLDING_WEIGHT]),
        sortLast: true
      }])
        .filter(d => d[HOLDING_WEIGHT] > 0.00001)
        .sort((a, b) => 
          ascending(!!a.sortLast, !!b.sortLast) ||
          ascending(b[HOLDING_WEIGHT], a[HOLDING_WEIGHT])
        )
    }))
)
const holdingSeries = createSelector(
  nestedHoldingsData,
  (props, state) => state.selectedHolding,
  (nestedData, selectedHolding) => nestedData.map(
    d => d.values.find(h => h[HOLDING_NAME] === selectedHolding) || { 
      [DATE]: d.values[0][DATE], 
      [HOLDING_WEIGHT]: 0 
    }
  ).map(d => ({ ...d, [HOLDING_WEIGHT]: [d[HOLDING_WEIGHT]] }))
)
const selectedPeriodData = createSelector(
  nestedHoldingsData,
  (props, state) => state.selectedPeriod,
  (nestedData, selectedPeriod) => nestedData.find(d => d.key === selectedPeriod).values
)
const percentTopN = createSelector(
  selectedPeriodData,
  (values) => sum(values, d => d[HOLDING_NAME] === REMAINING ? 0 : d[HOLDING_WEIGHT])
)
const selectedPeriodNumTopN = createSelector(
  selectedPeriodData,
  (values) => values.filter(d => d[HOLDING_NAME] !== REMAINING).length
)

class HoldingsWidget extends PureComponent {
  constructor() {
    super()
    this.state = {
      selectedPeriod: 'Current',
      selectedHolding: null,
      highlightedHolding: null,
      mouseDate: null,
      data: csvParse(window.fundHoldingsData, d => ({
        ...d,
        [DATE]: utcYear.floor(parseDate(d[DATE])),
        [HOLDING_WEIGHT]: +d[HOLDING_WEIGHT]
      }))
    }
    this.handleSelectPeriod = this.handleSelectPeriod.bind(this)
    this.handleSelectHolding = this.handleSelectHolding.bind(this)
    this.handleHighlightHolding = this.handleHighlightHolding.bind(this)
    this.handleMouseDate = this.handleMouseDate.bind(this)
  }
  handleSelectPeriod(selectedPeriod) {
    colorScale.domain([])
    this.setState({selectedPeriod})
  }
  handleSelectHolding(selectedHolding) {
    this.setState({selectedHolding})
  }
  handleHighlightHolding(highlightedHolding) {
    this.setState({highlightedHolding})
  }
  handleMouseDate(mouseDate) {
    this.setState({mouseDate})
  }
  render() {
    const { fundTicker, width, i18n, lang } = this.props
    const { selectedHolding, highlightedHolding, mouseDate } = this.state
    const nestedData = nestedHoldingsData(this.props, this.state)
    const numTopN = selectedPeriodNumTopN(this.props, this.state)
    const chartMarginLeft = 30
    const overlayContentInset = 40
    const overlayContentPadding = 20
    return (
      <div className='HoldingsWidget' >
        {/*<Selector
          onSelect={this.handleSelectPeriod}
          options={
            nestedData.map( (d, i) => {
              const id = d.key
              return { id: id, label: id }
            })
          }
          selected={selectedPeriod}
        />*/}
        <BreakdownPie
          data={selectedPeriodData(this.props, this.state)}
          width={width}
          nameField={HOLDING_NAME}
          weightField={HOLDING_WEIGHT}
          highlighted={highlightedHolding}
          onSelect={this.handleSelectHolding}
          onHighlight={this.handleHighlightHolding}
          colorScale={(name) => name === REMAINING ? otherColor : colorScale(name)}
          percentTopN={percentTopN(this.props, this.state)}
          numTopN={numTopN}
          weightOfTopLabel={`${translate('WEIGHT_OF_TOP_N_PREFIX', i18n, lang)} ${numTopN} ${translate('WEIGHT_OF_TOP_N_SUFFIX', i18n, lang)}`}
        />
        <BreakdownTable
          data={selectedPeriodData(this.props, this.state)}
          nameField={HOLDING_NAME}
          weightField={HOLDING_WEIGHT}
          highlighted={highlightedHolding}
          onSelect={this.handleSelectHolding}
          onHighlight={this.handleHighlightHolding}
          colorScale={(name) => name === REMAINING ? otherColor : colorScale(name)}
          nameColumnLabel={translate('NAME', i18n, lang)}
          weightColumnLabel={translate('WEIGHT_PCT', i18n, lang)}
        />
        { selectedHolding && width !== null ? 
          <div className='HoldingsWidget-overlay'>
            <div
              className='HoldingsWidget-overlay-content'
              style={{
                margin: overlayContentInset,
                padding: overlayContentPadding
              }}
            >
              <div
                className='HoldingsWidget-overlay-close'
                onClick={() => this.handleSelectHolding(null)}
              >×</div>
              <h2>{selectedHolding} (%) within {fundTicker} Since {nestedData[0].key}</h2>
              <Timeline
                width={width - overlayContentInset * 2 + chartMarginLeft - overlayContentPadding * 2}
                margin={{ t:10, r:80, b:30, l:chartMarginLeft }} 
                height={width/3.33}
                yDimension={HOLDING_WEIGHT}
                yFormat={'.2%'}
                data={holdingSeries(this.props, this.state)}
                mouseDate={mouseDate}
                onMouseDate={this.handleMouseDate} 
                yStartFromZero={true}
                curveInterpolation={curveStepAfter}
              />
            </div>
          </div> : null
        }
      </div>
    )
  }
}

export default HoldingsWidget


// WEBPACK FOOTER //
// ./src/HoldingsWidget.js